import "./MultiAccountEquityOrderWizard.scss";
import template from "./MultiAccountEquityOrderWizard.html";
import {AdviceTypes} from "../place-order-wizard/models/AdviceTypes";
import {IOnDestroy, IOnInit, IScope} from "angular";
import {IModalInstanceService} from "angular-ui-bootstrap";
import {CurrentUserService} from "../../../../services/user/CurrentUserService";
import {BehaviorSubject, Observable, ReplaySubject, Scheduler, Subject} from "rxjs";
import {PortfolioType} from "../../../models/Portfolio";
import {isNotNull} from "../../../../js/util/UtilFunctions";
import {AccountBriefRecord} from "../../../../account/types/AccountBriefRecord";
import {OrderAdviceService} from "../services/OrderAdviceService";
import {DuplicateOrderService} from "../services/DuplicateOrderService";
import {SequenceDuplicateVettingResponse} from "../types/DuplicateOrdersVettingResponse";
import {defaultAdvice, isSameAdvice, OrderAdvice} from "../place-order-wizard/models/OrderAdvice";
import {ObservableWithLazyLatest} from "../../../../js/classes/lazy-observable/ObservableWithLazyLatest";
import {UserConfig} from "../../../../types/UserConfig";
import {buildOrderRequest} from "./util/MultiAccountEquityWizardOrderUtil";
import {MultiOrderStore} from "./store/MultiOrderStore";
import {AdviceStore} from "./store/AdviceStore";
import {EquityWizardData} from "../place-equity-order-wizard/models/EquityWizardData";
import {MultiDuplicateOrdersNotificationDialog} from "./dialog/MultiDuplicateOrdersNotificationDialog";

interface Resolve {
    readonly order: EquityWizardData[];
}

export const MultiAccountEquityOrderWizard = "tfMultiAccountEquityOrderWizard";

export function placeMultiAccountEquityWizard() {
    return {
        template,
        bindings: {
            modalInstance: "<",
            resolve: "<"
        },
        controller: PlaceMultiAccountEquityOrderWizardController,
        controllerAs: "ctrl"
    };
}

export class PlaceMultiAccountEquityOrderWizardController implements IOnInit, IOnDestroy {

    /** @input */
    public readonly resolve: Resolve;

    /** @output */
    public accountBalance: number;

    /** @input */
    public readonly modalInstance: IModalInstanceService;

    public readonly hasAdvice$: Observable<boolean>;
    public readonly orderAdviceAccess$: Observable<boolean>;
    public readonly currentAccountBrief$: Observable<AccountBriefRecord>;
    public readonly adviceTypes$: Observable<AdviceTypes>;
    public readonly processingOrder$ = new BehaviorSubject(false);
    public readonly advice$ = new ReplaySubject<OrderAdvice>(1);
    private firstPortfolio$ = new ReplaySubject<AccountBriefRecord | undefined>(1);

    public store: MultiOrderStore;
    public adviceStore: AdviceStore;

    private readonly isAdviser$: Observable<boolean>;
    private readonly placeOrder$ = new Subject<boolean>();
    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    /** @ngInject */
    public constructor(private readonly $scope: IScope,
                       private readonly currentUserService: CurrentUserService,
                       private readonly orderAdviceService: OrderAdviceService,
                       private readonly duplicateOrderService: DuplicateOrderService,
                       private readonly multiDuplicateOrdersNotificationDialog: MultiDuplicateOrdersNotificationDialog,
                       private readonly userConfig: UserConfig) {
        this.isAdviser$ = this.buildIsAdviser();
        this.hasAdvice$ = this.buildHasAdvice();
        this.orderAdviceAccess$ = this.buildOrderAdviceAccess$();
        this.adviceTypes$ = this.buildAdviceTypes();
    }

    private buildHasAdvice(): Observable<boolean> {
        const source$ = Observable.combineLatest(this.firstPortfolio$, this.isAdviser$);
        return source$.filter(([portfolio]) => isNotNull(portfolio))
                      .map(([portfolio, isAdviser]) => isAdviser && (portfolio?.portfolioType === PortfolioType.BROKER))
                      .publishReplay(1)
                      .refCount();
    }

    private buildOrderAdviceAccess$(): Observable<boolean> {
        return this.currentUserService
            .current
            .takeUntil(this.destroyed$)
            .observeOn(Scheduler.async)
            .map((user) => user.hasOrderAdviceAccess)
            .publishReplay(1)
            .refCount();
    }

    private buildIsAdviser(): Observable<boolean> {
        return this.currentUserService
                   .current
                   .takeUntil(this.destroyed$)
                   .observeOn(Scheduler.async)
                   .map((user) => user.isAdviser())
                   .publishReplay(1)
                   .refCount();
    }

    private buildAdviceTypes(): Observable<AdviceTypes> {
        return this.firstPortfolio$
                   .takeUntil(this.destroyed$)
                   .filter(isNotNull)
                   .filter((a) => a.portfolioType === PortfolioType.BROKER)
                   .map((a) => a.id)
                   .distinctUntilChanged()
                   .switchMap((id) => this.fetchAdviceTypes(id))
                   .publishReplay(1)
                   .refCount();
    }

    private fetchAdviceTypes(portfolioId: number): Observable<AdviceTypes> {
        return Observable.of(portfolioId)
                         .flatMap((id) => this.orderAdviceService.get(id))
                         .catch((err) => Observable.empty<AdviceTypes>());
    }

    public async $onInit() {
        this.store = new MultiOrderStore();
        this.adviceStore = new AdviceStore();
        this.watchAdviceTypes();
        this.watchAdviceChange();
        this.watchPlaceOrder();
        this.watchOrderChanges();
    }

    public watchOrderChanges() {
        this.store.orders$
            .takeUntil(this.destroyed$)
            .observeOn(Scheduler.async)
            .flatMap((value) => value)
            .map( value => value.portfolio )
            .publishReplay(1)
            .refCount()
            .subscribe(v => this.firstPortfolio$.next(v));
    }

    private watchAdviceTypes() {
        const $source = ObservableWithLazyLatest.create(this.adviceTypes$, this.currentUserService.current)
                                                .map(([types, user]) => defaultAdvice(types, user));

        this.hasAdvice$
            .distinctUntilChanged()
            .takeUntil(this.destroyed$)
            .switchMap((hasAdvice) => hasAdvice ? $source : Observable.of(null))
            .subscribe((advice) => this.adviceStore.updateAdvice(advice));
    }

    private watchAdviceChange() {
        this.adviceStore
            .advice$
            .takeUntil(this.destroyed$)
            .map((o) => o)
            .filter(isNotNull)
            .distinctUntilChanged(isSameAdvice)
            .map((adv) => Object.assign({}, adv)) // Need to copy due to ng-model bindings sucking
            .subscribe((adv) => this.advice$.next(adv));
    }

    private watchPlaceOrder() {
        this.placeOrder$
            .takeUntil(this.destroyed$)
            .withLatestFrom(this.processingOrder$)
            .filter(([_, processing]) => !processing)
            .do(() => this.processingOrder$.next(true))
            .withLatestFrom(this.store.orders$, this.adviceStore.advice$, this.hasAdvice$, this.orderAdviceAccess$)
            .map(([_, order, advice, hasAdvice, hasAdviceAccess]) => {
                return order.map(v => {
                    return {
                        ...v,
                        advice: hasAdvice && hasAdviceAccess ? advice : undefined
                    } as EquityWizardData;
                });
            })
            .withLatestFrom(this.firstPortfolio$)
            .switchMap(([data, advice]) => this.validateRequest(data))
            .subscribe((data) => this.modalInstance.close(data));
    }

    /* Empty if the request should not be submitted, otherwise return the request to submit */
    private validateRequest(data: EquityWizardData[]) {
        if (data == null || data === []) {
            this.processingOrder$.next(false);
            return Observable.empty();
        }
        const request = buildOrderRequest(data);
        const result = this.duplicateOrderService.validateMultiple(request);
        return Observable.fromPromise(result)
                         .flatMap((res) => this.proceedIfDuplicates(res))
                         .do((proceed) => console.debug("Order should proceed?", proceed))
                         .do(() => this.processingOrder$.next(false))
                         .filter((proceed) => proceed)
                         .do(() => console.debug("Order proceeded"))
                         .map(() => data);
    }

    private proceedIfDuplicates(duplicates: SequenceDuplicateVettingResponse[]): Observable<boolean> {
        const duplicateResponses = duplicates
            .filter( d => d.success === true)
            .map( d => d.result)
            .filter( d => d.duplicates !== [] && d.duplicates.length > 0);
        if (duplicates.length === 0) {
            return Observable.of(true);
        } else if (duplicateResponses.length < 1) {
            return Observable.of(true);
        } else {
            return this.notifyDuplicates(duplicates);
        }
    }

    private notifyDuplicates(duplicates: SequenceDuplicateVettingResponse[]): Observable<boolean> {
        return this.multiDuplicateOrdersNotificationDialog.open(duplicates);
    }

    public adviceChanged(advice: OrderAdvice) {
        this.adviceStore.updateAdvice(advice);
    }

    public accountBalanceChanged(accountBalance: number) {
        this.accountBalance = accountBalance;
    }

    public placeOrder() {
        this.placeOrder$.next(true);
    }

    public cancel() {
        this.modalInstance.dismiss();
    }

    public $onDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
        this.store.destroy();
        this.adviceStore.destroy();
    }
}
